import axios from 'axios';
import React, { useEffect, useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import './AvailablePDF.css';
import { useNavigate } from 'react-router-dom';

export const AvailablePDF = () => {
  const [processedData, setProcessedData] = useState([]);
  const [totalBundle, setTotalBundle] = useState(0);
  const [totalProcessed, setTotalProcessed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'folder', direction: 'asc' });
  const [activeFilter, setActiveFilter] = useState('all');
  const [downloadingProcessed, setDownloadingProcessed] = useState(false);
  const [downloadingRemaining, setDownloadingRemaining] = useState(false);
  const navigate = useNavigate();

  useEffect(()=>{
    const login = sessionStorage.getItem("token");
    if(!login || undefined){
      alert("Login First");
      navigate("/adminP");
    }
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_ADMIN_API_URL}/processeddata`,{
            headers: {
              Authorization: `Bearer ${sessionStorage.getItem("token")}`
            }
          }
        );

        const { data: rows, processed, totalBundle: total } = response.data;

        // Fast O(1) lookup using Set — no backend status field relied upon
        const processedSet = new Set((processed || []).map(p => p.folder));

        // Compute status entirely on the frontend
        const updatedData = (rows || []).map(item => ({
          ...item,
          status: processedSet.has(item.assigned_bundles) ? 'completed' : 'pending',
        }));

        setProcessedData(updatedData);
        setTotalProcessed(processed?.length || 0);
        // API returns totalBundle as [{ 'count(*)': N }]
        setTotalBundle(total?.[0]?.['count(*)'] || 0);

        console.log('Rows:', updatedData);
        console.log('Processed:', processed);
        console.log('Total Bundle:', total);
      } catch (error) {
        console.error('Error fetching processed data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Derived count — totalBundle may be larger than assigned rows
  const totalRemaining = totalBundle - totalProcessed;

  // Filter data based on activeFilter
  const filteredData = useMemo(() => {
    let filtered = [...processedData];

    if (activeFilter === 'processed') {
      filtered = filtered.filter(item => item.status === 'completed');
    } else if (activeFilter === 'remaining') {
      filtered = filtered.filter(item => item.status !== 'completed');
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(item =>
        (item.assigned_bundles || '').toLowerCase().includes(q) ||
        (item.phone_number || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [activeFilter, processedData, searchQuery]);

  // Sorting logic
  const sortedData = useMemo(() => {
    const sortableItems = [...filteredData];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        let aValue = a[sortConfig.key] || '';
        let bValue = b[sortConfig.key] || '';

        if (sortConfig.key === 'status') {
          aValue = a.status === 'completed' ? 'Completed' : 'Pending';
          bValue = b.status === 'completed' ? 'Completed' : 'Pending';
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [filteredData, sortConfig]);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, itemsPerPage]);

  // Handle sort
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Handle page change
  const handlePageChange = (page) => setCurrentPage(page);

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  // Reset all filters
  const handleReset = () => {
    setSearchQuery('');
    setActiveFilter('all');
    setCurrentPage(1);
  };

  // Download Processed Excel
  const handleDownloadProcessed = async () => {
    setDownloadingProcessed(true);
    try {
      const processedBundles = processedData.filter(item => item.status === 'completed');

      if (processedBundles.length === 0) {
        alert('No processed data available to download');
        return;
      }

      const excelData = processedBundles.map((item, index) => ({
        'S.No': index + 1,
        'Bundle ID': item.assigned_bundles || '—',
        'Worker Name': item.worker_name || '—',
        'Phone Number': item.phone_number || '—',
        'Status': 'Completed',
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      const headers = Object.keys(excelData[0]);
      ws['!cols'] = headers.map(key => ({
        wch: Math.min(Math.max(key.length + 2, 12), 40),
      }));

      XLSX.utils.book_append_sheet(wb, ws, 'Processed Bundles');
      XLSX.writeFile(wb, `processed-bundles-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error downloading processed data:', error);
      alert('Failed to download processed data. Please try again.');
    } finally {
      setDownloadingProcessed(false);
    }
  };

  // Download Remaining Excel
  const handleDownloadRemaining = async () => {
    setDownloadingRemaining(true);
    try {
      const remainingBundles = processedData.filter(item => item.status !== 'completed');

      if (remainingBundles.length === 0) {
        alert('No remaining data available to download');
        return;
      }

      const excelData = remainingBundles.map((item, index) => ({
        'S.No': index + 1,
        'Bundle ID': item.assigned_bundles || '—',
        'Worker Name': item.worker_name || '—',
        'Phone Number': item.phone_number || '—',
        'Status': 'Pending',
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      const headers = Object.keys(excelData[0]);
      ws['!cols'] = headers.map(key => ({
        wch: Math.min(Math.max(key.length + 2, 12), 40),
      }));

      XLSX.utils.book_append_sheet(wb, ws, 'Remaining Bundles');
      XLSX.writeFile(wb, `remaining-bundles-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error downloading remaining data:', error);
      alert('Failed to download remaining data. Please try again.');
    } finally {
      setDownloadingRemaining(false);
    }
  };

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const isCompleted = status === 'completed';
    return (
      <span className={`badge-wrapper ${isCompleted ? 'badge-processed' : 'badge-pending'}`}>
        <span className="badge-dot"></span>
        {isCompleted ? '✓ Completed' : '○ Pending'}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="app-loader">
        <div className="loader-spinner"></div>
        <p className="loader-text">Loading dashboard data...</p>
      </div>
    );
  }

  return (
    <div className="pdf-viewer-root">
      {/* Background decoration */}
      <div className="bg-ornament ornament-1"></div>
      <div className="bg-ornament ornament-2"></div>

      <div className="main-content-wrap">
        {/* Header Section */}
        <header className="page-header">
          <div className="header-brand">
            <div className="brand-icon">📄</div>
            <div className="brand-text">
              <h1 className="brand-title">PDF Management Dashboard</h1>
              <p className="brand-subtitle">Track and monitor processed PDF bundles</p>
            </div>
          </div>
          <div className="header-actions">
            <button
              className="download-btn download-processed"
              onClick={handleDownloadProcessed}
              disabled={downloadingProcessed || totalProcessed === 0}
            >
              {downloadingProcessed ? (
                <>
                  <span className="btn-spinner"></span>
                  Downloading...
                </>
              ) : (
                <>
                  📥 Download Completed
                  <span className="btn-badge">{totalProcessed}</span>
                </>
              )}
            </button>
            <button
              className="download-btn download-remaining"
              onClick={handleDownloadRemaining}
              disabled={downloadingRemaining || totalRemaining === 0}
            >
              {downloadingRemaining ? (
                <>
                  <span className="btn-spinner"></span>
                  Downloading...
                </>
              ) : (
                <>
                  📥 Download Pending
                  <span className="btn-badge">{totalRemaining}</span>
                </>
              )}
            </button>
            <button className="back-btn" onClick={() => navigate(-1)}>
              ⬅ Back
            </button>
          </div>
        </header>

        {/* Statistics Cards */}
        <div className="stats-panel">
          <div className="stat-item stat-total">
            <div className="stat-icon">📊</div>
            <div className="stat-details">
              <div className="stat-number">{totalBundle}</div>
              <div className="stat-label">Total Bundles</div>
            </div>
          </div>
          <div className="stat-item stat-processed">
            <div className="stat-icon">✅</div>
            <div className="stat-details">
              <div className="stat-number">{totalProcessed}</div>
              <div className="stat-label">Completed</div>
              <div className="stat-progress">
                <div
                  className="progress-fill"
                  style={{ width: totalBundle ? `${(totalProcessed / totalBundle) * 100}%` : '0%' }}
                ></div>
              </div>
            </div>
          </div>
          <div className="stat-item stat-remaining">
            <div className="stat-icon">⏳</div>
            <div className="stat-details">
              <div className="stat-number">{totalRemaining}</div>
              <div className="stat-label">Pending</div>
            </div>
          </div>
        </div>

        {/* Control Bar */}
        <div className="control-bar">
          <div className="filter-group">
            <button
              className={`filter-button ${activeFilter === 'all' ? 'filter-active' : ''}`}
              onClick={() => { setActiveFilter('all'); setCurrentPage(1); }}
            >
              All
              <span className="filter-counter">{totalBundle}</span>
            </button>
            {/* <button
              className={`filter-button ${activeFilter === 'processed' ? 'filter-active' : ''}`}
              onClick={() => { setActiveFilter('processed'); setCurrentPage(1); }}
            >
              Completed
              <span className="filter-counter">{totalProcessed}</span>
            </button> */}
            <button
              className={`filter-button ${activeFilter === 'remaining' ? 'filter-active' : ''}`}
              onClick={() => { setActiveFilter('remaining'); setCurrentPage(1); }}
            >
              Pending
              <span className="filter-counter">{totalRemaining}</span>
            </button>
          </div>

          <div className="search-group">
            <div className="search-wrapper">
              <input
                type="text"
                className="search-field"
                placeholder="Search by Bundle ID or Phone..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              />
              <span className="search-icon-svg">🔍</span>
              {searchQuery && (
                <button className="search-clear-btn" onClick={() => setSearchQuery('')}>
                  ✕
                </button>
              )}
            </div>
            <button className="reset-button" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-section">
          <div className="table-toolbar">
            <div className="table-info">
              Showing {paginatedData.length} of {filteredData.length} items
            </div>
            <div className="items-selector">
              <label>Show</label>
              <select className="items-dropdown" value={itemsPerPage} onChange={handleItemsPerPageChange}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <label>entries</label>
            </div>
          </div>

          {filteredData.length > 0 ? (
            <>
              <div className="table-responsive">
                <table className="data-grid">
                  <thead className="grid-header">
                    <tr>
                      <th className="sortable-col" onClick={() => handleSort('assigned_bundles')}>
                        Bundle ID
                        <span className="sort-arrow">
                          {sortConfig.key === 'assigned_bundles' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </span>
                      </th>
                      <th className="sortable-col" onClick={() => handleSort('phone_number')}>
                        Phone Number
                        <span className="sort-arrow">
                          {sortConfig.key === 'phone_number' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </span>
                      </th>
                      <th className="sortable-col" onClick={() => handleSort('worker_name')}>
                        Worker Name
                        <span className="sort-arrow">
                          {sortConfig.key === 'worker_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </span>
                      </th>
                      <th className="sortable-col" onClick={() => handleSort('status')}>
                        Status
                        <span className="sort-arrow">
                          {sortConfig.key === 'status' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="grid-body">
                    {paginatedData.map((item, index) => {
                      const isCompleted = item.status === 'completed';
                      return (
                        <tr
                          key={`${item.assigned_bundles}-${index}`}
                          className={isCompleted ? 'row-completed' : 'row-pending'}
                        >
                          <td className="bundle-cell">
                            <span className="bundle-code">{item.assigned_bundles || '—'}</span>
                          </td>
                          <td className="phone-cell">{item.phone_number || '—'}</td>
                          <td className="worker-cell">{item.worker_name || '—'}</td>
                          <td className="status-cell">
                            <StatusBadge status={item.status} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-bar">
                  <button
                    className="pagination-nav"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    ← Previous
                  </button>
                  <div className="pagination-numbers">
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          className={`pagination-number ${currentPage === pageNum ? 'number-active' : ''}`}
                          onClick={() => handlePageChange(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    className="pagination-nav"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="empty-results">
              <div className="empty-icon">📭</div>
              <h3 className="empty-title">No data found</h3>
              <p className="empty-message">No bundles match your current filter criteria.</p>
              <button className="reset-button reset-button-large" onClick={handleReset}>
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
